import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { authApi, hasActiveSession } from '../../services/plotfyApi.js'

const AuthContext = createContext(null)

function normalizeUser(source) {
  if (!source) return null
  const user = source.user ?? source
  const profile = source.profile ?? user.profile ?? source.creatorProfile ?? source.creator_profile ?? source.brandProfile ?? source.brand_profile ?? {}
  const creatorProfile = {
    ...profile,
    reviewStatus: profile.reviewStatus ?? profile.review_status ?? (user.demo ? 'APPROVED' : 'PENDING'),
    tier: profile.tier ?? 'SILVER',
    rejectionReason: profile.rejectionReason ?? profile.rejection_reason ?? null,
  }

  const isGoogleUser = Boolean(
    (user.googleSubject ?? user.google_subject ?? source.googleSubject ?? source.google_subject) &&
    !(user.hasPassword ?? user.has_password ?? source.hasPassword ?? source.has_password)
  )

  return {
    ...user,
    profile,
    creatorProfile,
    id: user.publicId ?? user.public_id ?? user.id,
    name: user.displayName ?? user.display_name ?? user.name ?? 'Pengguna Plotfy',
    email: user.email ?? '',
    avatar: user.avatarUrl ?? user.avatar_url ?? null,
    role: user.role ? String(user.role).toUpperCase() : null,
    status: user.status ?? 'ACTIVE',
    permissions: Array.isArray(user.permissions)
      ? user.permissions
      : Array.isArray(source.permissions)
      ? source.permissions
      : [],
    profileComplete: user.profileComplete ?? user.profile_complete ?? (
      !isGoogleUser && user.role
        ? true
        : user.role === 'ADMIN' || user.role === 'SUPERADMIN'
        ? true
        : user.role === 'CREATOR'
        ? Boolean(profile.whatsapp && profile.age != null)
        : user.role === 'BRAND'
        ? Boolean((profile.brandName || user.displayName || user.name) && (profile.whatsapp || user.whatsapp))
        : false
    ),
  }
}

export function roleHomeFor(role, profileComplete = true) {
  if (!profileComplete) return '/onboarding'
  const paths = {
    CREATOR: '/creator',
    BRAND: '/brand',
    ADMIN: '/admin',
    SUPERADMIN: '/admin',
  }
  return paths[role] || '/onboarding'
}

function demoUser(role) {
  const names = {
    CREATOR: 'Nadia Creator',
    BRAND: 'Nala Coffee',
    ADMIN: 'Admin Plotfy',
    SUPERADMIN: 'Super Admin Plotfy',
  }
  return {
    id: `demo-${role.toLowerCase()}`,
    name: names[role] || 'Pengguna Demo',
    email: `${role.toLowerCase()}@demo.plotfy.id`,
    role,
    status: 'ACTIVE',
    profileComplete: true,
    demo: true,
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [serviceAvailable, setServiceAvailable] = useState(true)

  const refreshUser = useCallback(async () => {
    const demoRole = typeof window !== 'undefined' && window.sessionStorage
      ? sessionStorage.getItem('plotfy:demo-role')
      : null

    if (!hasActiveSession()) {
      if (demoRole) {
        setUser(demoUser(demoRole))
      } else {
        setUser(null)
      }
      setServiceAvailable(true)
      setLoading(false)
      return demoRole ? demoUser(demoRole) : null
    }

    try {
      const nextUser = normalizeUser(await authApi.me())
      setUser(nextUser)
      setServiceAvailable(true)
      return nextUser
    } catch (error) {
      if (error.status === 401) {
        setUser(null)
        setServiceAvailable(true)
      } else if (error.status === 403) {
        // Do not reset user on 403: user is authenticated but restricted
        setServiceAvailable(true)
      } else {
        setServiceAvailable(false)
        if (demoRole) setUser(demoUser(demoRole))
      }
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refreshUser()
  }, [refreshUser])

  useEffect(() => {
    const handleUnauthorized = () => {
      sessionStorage.removeItem('plotfy:demo-role')
      setUser(null)
    }
    window.addEventListener('plotfy:unauthorized', handleUnauthorized)
    return () => window.removeEventListener('plotfy:unauthorized', handleUnauthorized)
  }, [])

  const loginWithGoogle = useCallback(async (credential) => {
    const nextUser = normalizeUser(await authApi.google(credential))
    setUser(nextUser)
    setServiceAvailable(true)
    return nextUser
  }, [])

  const loginWithEmail = useCallback(async (email, preferredRole = 'CREATOR') => {
    const trimmed = String(email).trim().toLowerCase()
    let detectedRole = preferredRole
    if (trimmed.includes('admin')) detectedRole = 'ADMIN'
    else if (trimmed.includes('brand') || trimmed.includes('nala') || trimmed.includes('shop')) detectedRole = 'BRAND'
    else if (trimmed.includes('creator') || trimmed.includes('nadia') || trimmed.includes('user')) detectedRole = 'CREATOR'

    if (!serviceAvailable || trimmed.includes('demo')) {
      const demoRes = demoUser(detectedRole)
      demoRes.email = trimmed
      sessionStorage.setItem('plotfy:demo-role', detectedRole)
      setUser(demoRes)
      return demoRes
    }

    try {
      const res = await authApi.email(trimmed, detectedRole)
      const nextUser = normalizeUser(res)
      setUser(nextUser)
      setServiceAvailable(true)
      return nextUser
    } catch {
      const fallbackUser = demoUser(detectedRole)
      fallbackUser.email = trimmed
      sessionStorage.setItem('plotfy:demo-role', detectedRole)
      setUser(fallbackUser)
      return fallbackUser
    }
  }, [serviceAvailable])

  const loginWithPassword = useCallback(async (username, password) => {
    const res = await authApi.login(username, password)
    const nextUser = normalizeUser(res)
    setUser(nextUser)
    setServiceAvailable(true)
    return nextUser
  }, [])

  const registerWithPassword = useCallback(async (payload) => {
    const res = await authApi.register(payload)
    const nextUser = normalizeUser(res)
    setUser(nextUser)
    setServiceAvailable(true)
    return nextUser
  }, [])

  const chooseRole = useCallback(async (role) => {
    const normalizedRole = String(role).toUpperCase()
    if (user?.demo) {
      const nextUser = demoUser(normalizedRole)
      sessionStorage.setItem('plotfy:demo-role', normalizedRole)
      setUser(nextUser)
      return nextUser
    }
    const nextUser = normalizeUser(await authApi.chooseRole(normalizedRole))
    setUser(nextUser)
    return nextUser
  }, [user?.demo])

  const completeOnboarding = useCallback(async (payload) => {
    const res = await authApi.completeOnboarding(payload)
    const nextUser = normalizeUser(res)
    setUser(nextUser)
    return nextUser
  }, [])

  const enterDemo = useCallback((role) => {
    const normalizedRole = String(role).toUpperCase()
    sessionStorage.setItem('plotfy:demo-role', normalizedRole)
    const nextUser = demoUser(normalizedRole)
    setUser(nextUser)
    return nextUser
  }, [])

  const logout = useCallback(async () => {
    sessionStorage.removeItem('plotfy:demo-role')
    if (!user?.demo) {
      try {
        await authApi.logout()
      } catch {
        // Local session is cleared even when the server is unreachable.
      }
    }
    setUser(null)
  }, [user?.demo])

  const value = useMemo(() => ({
    user,
    loading,
    serviceAvailable,
    roleHome: roleHomeFor(user?.role, user?.profileComplete),
    loginWithGoogle,
    loginWithPassword,
    registerWithPassword,
    loginWithEmail,
    chooseRole,
    completeOnboarding,
    enterDemo,
    logout,
    refreshUser,
  }), [chooseRole, completeOnboarding, enterDemo, loading, loginWithEmail, loginWithGoogle, loginWithPassword, logout, refreshUser, registerWithPassword, serviceAvailable, user])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth harus digunakan di dalam AuthProvider')
  return context
}
